import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { useDashboards } from '@Hooks/useDashboards';
import { Pencil, Percent, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import ConfirmationDialog from '@Components/ConfirmationDialog';
import service from '../../../service';
import { Dashboard } from '../../../types/dashboard';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@Contexts/auth';

const DASHBOARD_FIELD_MAPPING: Record<string, string> = {
  name: 'filter[name]',
  description: 'filter[description]',
  embeddableResource: 'filter[embeddableResource]',
};

const columnConfigs: ColumnConfig<Dashboard>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name ?? '-',
    type: 'text',
  },
  {
    id: 'description',
    headerLabel: 'Description',
    accessor: (row) => row.description ?? '-',
    type: 'text',
  },
  {
    id: 'embeddableResource',
    headerLabel: 'Resource',
    accessor: (row) => row.embeddableResource ?? '-',
    type: 'text',
  },
];

type DashboardsListProps = {
  onEditClick?: (row: { original: Dashboard }) => void;
  onCreateClick?: () => void;
};

export default function DashboardsList({
  onEditClick,
  onCreateClick,
}: DashboardsListProps = {}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dashboardToDelete, setDashboardToDelete] = useState<Dashboard | null>(
    null,
  );

  const { user } = useAuth();

  const client = useQueryClient();

  const controller = useResourceTableController<Dashboard>({
    columnsConfig: columnConfigs,
    defaultSort: {
      id: 'name',
      desc: false,
    },
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: DASHBOARD_FIELD_MAPPING,
      }),
    quickFilterFields: [
      { field: 'name', label: 'Name' },
      { field: 'description', label: 'Description' },
      { field: 'embeddableResource', label: 'Resource' },
    ],
    initialPageSize: 10,
    rowActions: (row) => (
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          // asChild
          onClick={(e) => {
            e.stopPropagation();
            onEditClick({ original: row });
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(row);
          }}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    ),
  });

  const {
    data: dashboards,
    status,
    isError,
    error,
  } = useDashboards({ searchParams: controller.searchParams });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error ? error.message : 'Failed to load Dashboards',
      );
    }
  }, [isError, error]);

  const rows: Dashboard[] = dashboards?.data ?? [];
  const meta = dashboards?.meta;

  const handleDeleteClick = (dashboard: Dashboard) => {
    setDashboardToDelete(dashboard);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!dashboardToDelete) return;

    try {
      await service.deleteDashboard(dashboardToDelete.id, user?.id);

      toastSuccess(`"${dashboardToDelete.name}" has been deleted.`);
      setDeleteDialogOpen(false);
      setDashboardToDelete(null);
      client.invalidateQueries({ queryKey: ['dashboards'] });
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to delete Dashboard',
      );
    }
  };

  return (
    <div className="mt-2 space-y-4">
      <ResourceTableView<Dashboard>
        label="Dashboards"
        controller={controller}
        data={rows}
        meta={meta}
        status={status}
        summaryIcon={<Percent className="h-4 w-4 text-zinc-500" />}
        summaryLabel="Total Dashboards: "
        renderExtraActions={() => (
          <Button
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              onCreateClick();
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Dashboard
          </Button>
        )}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSubmit={handleDeleteConfirm}
        match={dashboardToDelete?.name ?? ''}
        title="Delete Dashboard"
        description={`Are you sure you want to delete "${dashboardToDelete?.name}"? This action cannot be undone.`}
        matchLabel="Type the dashboard name to confirm"
        submitLabel="Delete"
        // isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
